import { useTimelineContext } from '@/hooks/useTimelineContext';
import { alert } from '@/services/alert';
import { TIMELINE_FOCUS_ITEM_QUERY_PARAM } from '@bariendo/lib/constants';
import { createReference, getReferenceString } from '@medplum/core';
import { Practitioner, Reference, Resource } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router';
import { StandalonePatientAccessCardKind } from './patientAccessCardKind';

/**
 * Starts a call card the rep picked from the chart-note list, with no appointment
 * behind it.
 *
 * The hand-off was briefly attached to the NP consult, which made the appointment
 * decide which chart note the user got — a coordinator on the consult was handed a
 * hand-off script and an NP wanting an H&P lost the generic chart-note link. The
 * user picks instead, and the card is created on demand here.
 *
 * "Link to an appointment" is passed through as `basedOn` when the rep picks one:
 * without it the card is unreachable from that appointment's Chart row, which keeps
 * offering "Start …" and creates a second card on the next click.
 *
 * With no appointment there is no `based-on` idempotency key, so the create keys on
 * the patient's *open* card of that category: a double click cannot produce two, and
 * a second call after the first has been charted still gets one of its own.
 *
 * Refreshes the timeline before focusing the new card. Medplum's WebSocket
 * subscriptions do not replay, so a card created before the timeline's subscription
 * is established is never pushed, and the focus then lands on an item the timeline
 * has not fetched (BAR-2741). `fetchData` replaces rather than appends, so this
 * does not duplicate rows.
 */
export function useCreatePatientAccessCard(): {
  startCard: (kind: StandalonePatientAccessCardKind, basedOn?: Reference<Resource>) => Promise<void>;
  isCreating: boolean;
} {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner | undefined;
  const [searchParams, setSearchParams] = useSearchParams();
  const { patientContext, timelineContext } = useTimelineContext();
  const [isCreating, setIsCreating] = useState(false);

  const patient = patientContext?.patient;
  const { refresh: refreshTimeline } = timelineContext;

  const startCard = useCallback(
    async (kind: StandalonePatientAccessCardKind, basedOn?: Reference<Resource>) => {
      if (!patient?.id || isCreating) return;

      setIsCreating(true);
      try {
        const note = await kind.create(medplum, {
          patient: createReference(patient),
          ...(basedOn ? { basedOn } : {}),
          ...(profile ? { sender: createReference(profile) } : {}),
          sent: new Date().toISOString(),
        });

        const reference = getReferenceString(note);
        if (reference) {
          refreshTimeline();
          const nextParams = new URLSearchParams(searchParams);
          nextParams.set(TIMELINE_FOCUS_ITEM_QUERY_PARAM, reference);
          setSearchParams(nextParams);
        }
      } catch (error) {
        alert.error(`Couldn't start the card: ${error instanceof Error ? error.message : 'Unknown error'}`);
      } finally {
        setIsCreating(false);
      }
    },
    [medplum, patient, profile, isCreating, searchParams, setSearchParams, refreshTimeline]
  );

  return { startCard, isCreating };
}
